<template>
    <v-menu
        v-model="menu"
        offset-y
        left
        :close-on-content-click="false"
        min-width="340"
        max-width="380"
    >
        <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" aria-label="Unread comments">
                <v-badge :content="badgeCount" :value="unreadCommentCount > 0" color="error" overlap>
                    <v-icon>{{ unreadCommentCount > 0 ? 'mdi-bell' : 'mdi-bell-outline' }}</v-icon>
                </v-badge>
            </v-btn>
        </template>

        <v-card class="bell-card" :class="{ 'is-dark': $vuetify.theme.dark }">
            <div class="bell-header">
                <span class="bell-title">Unread Comments</span>
                <v-btn text x-small :disabled="!unreadComments.length" @click="markAllRead">Mark all read</v-btn>
            </div>
            <div class="bell-body">
                <div v-if="unreadComments.length">
                    <div v-for="item in unreadComments" :key="item.entityType + '-' + item.entityKeyId" class="uc-row">
                        <a
                            class="row-link"
                            :href="hrefFor(item)"
                            @click="open(item, $event)"
                            @auxclick.middle="markSeen(item, 'Could not mark this item as read.')"
                        >
                            <span class="stack">
                                <v-chip x-small label class="type-chip">{{ typeLabel(item.entityType) }}</v-chip>
                                <span class="name">{{ item.entityName }}</span>
                                <span class="count">{{ item.newCount }} new {{ item.newCount === 1 ? 'comment' : 'comments' }} - {{ item.latestCommentDate | luxonPostedAgo }}</span>
                            </span>
                            <v-icon small class="chev">mdi-chevron-right</v-icon>
                        </a>
                        <v-btn icon x-small class="dismiss" title="Dismiss" @click.stop="dismiss(item)">
                            <v-icon small>mdi-close</v-icon>
                        </v-btn>
                    </div>
                </div>
                <div v-else class="empty-inline">No unread comments.</div>

                <v-expansion-panels v-model="historicPanel" flat class="historic-panels">
                    <v-expansion-panel>
                        <v-expansion-panel-header class="historic-header">Earlier comments (last 60 days)</v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <div v-if="historicLoading" class="empty-inline">Loading…</div>
                            <div v-else-if="historicError" class="empty-inline">
                                Couldn't load earlier comments.
                                <v-btn icon x-small @click="fetchHistoric"><v-icon small>mdi-refresh</v-icon></v-btn>
                            </div>
                            <div v-else-if="historic && historic.length">
                                <div v-for="item in historic" :key="'h-' + item.entityType + '-' + item.entityKeyId" class="uc-row">
                                    <a class="row-link" :href="hrefFor(item)" @click="openHistoric(item, $event)">
                                        <span class="stack">
                                            <v-chip x-small label class="type-chip">{{ typeLabel(item.entityType) }}</v-chip>
                                            <span class="name">{{ item.entityName }}</span>
                                            <span class="count muted">{{ item.newCount }} {{ item.newCount === 1 ? 'Comment' : 'Comments' }} - {{ item.latestCommentDate | luxonPostedAgo }}</span>
                                        </span>
                                        <v-icon small class="chev">mdi-chevron-right</v-icon>
                                    </a>
                                </div>
                            </div>
                            <div v-else class="empty-inline">No earlier comments.</div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </div>
        </v-card>
    </v-menu>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import api from '@/api'

// Stored EntityType is the domain class name (verified against the DB). Note LammieRequest, not Lammie.
const TYPE_MAP = {
    Encounter: { route: 'readEncounter', label: 'Encounter' },
    Plot: { route: 'readPlot', label: 'Plot' },
    Monster: { route: 'readMonster', label: 'Monster' },
    Rumour: { route: 'readRumour', label: 'Rumour' },
    LammieRequest: { route: 'readLammie', label: 'Lammie' },
    RitualCircle: { route: 'readRitualCircle', label: 'Ritual Circle' },
    RitualCircleNetwork: { route: 'readNetwork', label: 'Network' },
    ResearchRequest: { route: 'readResearch', label: 'Research' },
    // Keyed 'Page', not 'SystemPage': ActivityLog stores the domain class name, and the
    // System Pages domain class is Page. A 'SystemPage' key never matched, so those rows
    // rendered with no href and a click that marked them read but navigated nowhere.
    Page: { route: 'readSystemPage', label: 'System Page' },
}

export default {
    name: 'NotificationBell',
    data() {
        return {
            menu: false,
            historicPanel: undefined,
            historic: null,
            historicLoading: false,
            historicError: null,
            historicLoaded: false,
        }
    },
    computed: {
        ...mapState(['unreadComments']),
        ...mapGetters(['unreadCommentCount']),
        // Cap the pill so a three-digit count can't widen it into the avatar button.
        badgeCount() {
            return this.unreadCommentCount > 99 ? '99+' : this.unreadCommentCount
        },
    },
    watch: {
        menu(open) {
            if (open) {
                this.$store.dispatch('fetchUnreadComments')
                // Only refresh what the user can actually see. A collapsed panel
                // reloads when it is next expanded (see historicPanel below).
                if (this.historicLoaded && this.historicPanel === 0) this.fetchHistoric()
            }
        },
        historicPanel(val) {
            // Expanding always reloads, so a panel left collapsed across bell
            // opens is never shown with data fetched some time ago.
            if (val === 0 && !this.historicLoading) this.fetchHistoric()
        },
    },
    mounted() {
        this.$store.dispatch('fetchUnreadComments')
    },
    methods: {
        typeLabel(entityType) {
            const m = TYPE_MAP[entityType]
            return m ? m.label : entityType
        },
        routeFor(item) {
            const m = TYPE_MAP[item.entityType]
            return m ? { name: m.route, params: { id: item.entityKeyId }, query: { tab: 'comments' } } : null
        },
        // Real href so middle-click / ctrl-click opens the item in a new tab.
        hrefFor(item) {
            const route = this.routeFor(item)
            return route ? this.$router.resolve(route).href : null
        },
        // True when the browser should handle the click itself (new tab/window).
        // Middle-click never lands here — Chrome raises auxclick for it, not click.
        isModifiedClick(event) {
            return !event || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0
        },
        refreshUnread() {
            return this.$store.dispatch('fetchUnreadComments')
        },
        async fetchHistoric() {
            this.historicLoading = true
            this.historicError = null
            try {
                const data = await api.get('dashboard/historicComments')
                this.historic = data
                this.historicLoaded = true
            } catch (err) {
                this.historicError = err
            } finally {
                this.historicLoading = false
            }
        },
        async markSeen(item, errorText) {
            try {
                await api.post(`dashboard/unreadComments/${item.entityType}/${item.entityKeyId}/markSeen`)
                await this.refreshUnread()
            } catch (err) {
                this.showSnackbar('error', errorText)
            }
        },
        open(item, event) {
            // Mark read either way, then navigate straight away — the bell lives in the
            // nav bar, so it survives the route change and the refresh lands behind it.
            this.markSeen(item, 'Could not mark this item as read.')
            if (this.isModifiedClick(event)) return
            event.preventDefault()
            this.menu = false
            const route = this.routeFor(item)
            if (route) this.$router.push(route).catch(() => {})
        },
        dismiss(item) {
            return this.markSeen(item, 'Could not dismiss this item.')
        },
        async markAllRead() {
            try {
                await api.post('dashboard/unreadComments/markAllSeen')
                await this.refreshUnread()
            } catch (err) {
                this.showSnackbar('error', 'Could not mark all as read.')
            }
        },
        openHistoric(item, event) {
            if (this.isModifiedClick(event)) return
            event.preventDefault()
            this.menu = false
            const route = this.routeFor(item)
            if (route) this.$router.push(route).catch(() => {})
        },
    },
}
</script>

<style scoped>
.bell-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid rgba(0,0,0,0.08);
}
.bell-title { font-weight: 600; font-size: 14px; }
.bell-body { max-height: 60vh; overflow-y: auto; padding: 8px 12px; }
.uc-row { display: flex; align-items: center; gap: 8px; padding: 4px 0; border-bottom: 1px dotted #ddd; font-size: 13px; }
.uc-row:last-child { border-bottom: none; }
.row-link {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    padding: 2px 0;
    cursor: pointer;
    color: inherit;
    text-decoration: none;
}
.row-link:hover { background: rgba(25,118,210,0.04); }
.stack { display: flex; flex-direction: column; align-items: flex-start; gap: 1px; flex: 1; min-width: 0; }
.type-chip { text-transform: uppercase; margin-bottom: 1px; }
.name { font-weight: 500; overflow-wrap: anywhere; }
.count { color: #1976d2; font-weight: 600; white-space: normal; }
.count.muted { color: rgba(0,0,0,0.55); font-weight: 400; }
.chev { color: rgba(0,0,0,0.4); }
.empty-inline { color: rgba(0,0,0,0.54); font-style: italic; padding: 4px 0; }
.historic-panels { margin-top: 8px; }
.historic-panels >>> .v-expansion-panel {
    background-color: rgba(0,0,0,0.05);
    border: 1px solid rgba(0,0,0,0.12);
    border-radius: 4px;
}
.historic-panels >>> .v-expansion-panel-header { min-height: 32px; padding: 4px 12px; }
.historic-panels >>> .v-expansion-panel-content__wrap { padding: 0 12px 8px; }
.historic-header { font-size: 12px; }
.bell-card.is-dark .bell-header { border-bottom-color: rgba(255,255,255,0.12); }
.bell-card.is-dark .uc-row { border-bottom-color: rgba(255,255,255,0.18); }
.bell-card.is-dark .chev { color: rgba(255,255,255,0.5); }
.bell-card.is-dark .count.muted { color: rgba(255,255,255,0.6); }
.bell-card.is-dark .empty-inline { color: rgba(255,255,255,0.6); }
.bell-card.is-dark .historic-panels >>> .v-expansion-panel {
    background-color: rgba(255,255,255,0.06);
    border-color: rgba(255,255,255,0.16);
}
</style>
